import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  setDocumentMeta, setCanonical, setOgImage, injectJsonLdList,
  buildWebPageSchema, buildFaqPageSchema, buildBreadcrumbSchema,
} from '@/lib/seo';
import { ContentPage } from '@/components/marketing/content-page';

const FAQS = [
  {
    q: 'What is a customer portal in a help desk?',
    a: 'A customer portal is a branded page where your own customers sign in, see the requests they have raised, reply to your team, attach files and open new requests, without needing an account on your help desk. In Kora it is switched on from Settings and lives at usekora.app/p/portal/your-company.',
  },
  {
    q: 'Do my customers need a Kora account or a password?',
    a: 'No. Customers sign in with a link sent to their email address, so there is no account to create and no password to remember. You can let anyone with an email address sign in, or restrict the portal to invited customers only.',
  },
  {
    q: 'What can customers see in the portal?',
    a: 'Only the requests they reported themselves. They can reply, reopen a request, attach files (up to 10 MB, 5 per message), raise a new request and update their profile. Internal notes written by your team are never shown or emailed to customers.',
  },
  {
    q: 'How much does the Kora customer portal cost?',
    a: 'The portal has no seat or plan fee. A new request raised through the portal is a normal ticket and costs 1 credit ($0.10). Browsing, replying, attachments, data export and data deletion are free. New workspaces start with 1,100 free credits.',
  },
  {
    q: 'Can I brand the portal with my logo and colors?',
    a: 'Yes. Your logo, colors and fonts are set once for the workspace and shared by the portal, the help center and customer emails. Customer emails carry a "Powered by Kora" line. Custom domains and SSO are planned, not available yet.',
  },
  {
    q: 'Can customers download or delete their data?',
    a: 'Yes. Customers can download their data as JSON or CSV and delete it themselves. You can also export or erase a customer’s data on their behalf, and every data request is recorded in an audit log. Kora gives you the tools; how you meet your own legal obligations remains your responsibility.',
  },
  {
    q: 'Can I control who uses the portal and how much?',
    a: 'Yes. Owners can turn the portal on or off, choose between open sign-in and invite-only, set hourly and daily request caps, and block individual customers.',
  },
  {
    q: 'Do portal pages show up in Google?',
    a: 'No. Portal pages are set to noindex by design, so your customers’ requests are never listed in search results.',
  },
];

export function CustomerPortalSoftwarePage() {
  useEffect(() => {
    const title = 'Customer Portal Software, $0.10 Per Request | Kora';
    const description = 'Branded customer portal for your help desk: emailed sign-in, customers see only their own requests, reply and raise new ones. No seat fees, 1,100 free credits.';
    setDocumentMeta(title, description);
    setCanonical('/customer-portal-software');
    setOgImage('/assets/og-default.png');

    const cleanup = injectJsonLdList([
      buildWebPageSchema('/customer-portal-software', title, description),
      buildBreadcrumbSchema([
        { name: 'Home', url: 'https://usekora.app/' },
        { name: 'Customer portal software', url: 'https://usekora.app/customer-portal-software' },
      ]),
      buildFaqPageSchema(FAQS),
    ]);

    return cleanup;
  }, []);

  return (
    <ContentPage
      pill="Feature guide"
      title="Customer portal software your customers can use in one click"
      subtitle="Give customers a branded place to raise requests, follow progress and reply, with passwordless sign-in and no per-seat fees."
      answer={
        <>
          <strong>Kora&apos;s customer portal is off by default and takes one toggle to turn on.</strong> Customers sign in
          with an emailed link (no account, no password), see only their own requests, and can reply, attach files and raise
          new ones. Browsing and replying are free; a new request is one ticket, 1 credit ($0.10).
        </>
      }
      ctaTitle="Turn on your portal in minutes"
      ctaSubtitle="Pay as you go: $1 = 10 credits, 1 credit per ticket. Start with 1,100 free credits. No card."
    >
      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mt-4 mb-3">
        Why a customer portal beats a shared inbox
      </h2>
      <p className="text-sm text-zinc-600 leading-relaxed mb-4">
        Without a portal, customers ask &quot;any update?&quot; by email and your team searches threads to answer. A portal turns
        that into one page per customer: every request, its status and the full conversation, always current. Customers
        stop chasing, and your team stops answering status questions.
      </p>
      <p className="text-sm text-zinc-600 leading-relaxed mb-6">
        Most portals also arrive with extra cost: another seat tier, a per-customer fee or a sales call. Kora&apos;s portal is a
        setting on the same workspace, billed the way everything in Kora is billed, by the ticket.
      </p>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mb-3">
        What the Kora customer portal includes
      </h2>
      <ul className="space-y-2 text-sm text-zinc-600 mb-6 list-disc pl-5">
        <li><strong>Passwordless sign-in:</strong> customers get an emailed link. No Kora account, no password.</li>
        <li><strong>Only their own requests:</strong> each customer sees what they reported, nothing else.</li>
        <li><strong>Reply, reopen and attach:</strong> files up to 10 MB, 5 per message.</li>
        <li><strong>Raise new requests:</strong> a new request becomes a normal ticket in your queue.</li>
        <li><strong>Internal notes stay internal:</strong> your team chooses reply-to-customer or internal note; internal notes are never shown or emailed to customers.</li>
        <li><strong>Your branding:</strong> logo, colors and fonts shared with the help center and customer emails.</li>
        <li><strong>Self-service data rights:</strong> customers can download their data (JSON, CSV) and delete it.</li>
        <li><strong>Owner controls:</strong> on or off, open or invite-only sign-in, hourly and daily caps, block customers, and an audit log of data requests.</li>
      </ul>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mb-3">
        What it costs
      </h2>
      <div className="overflow-x-auto rounded-2xl border border-zinc-200 my-6">
        <table className="w-full min-w-[480px] text-sm bg-white ">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50 ">
              <th className="text-left px-4 py-3 font-semibold text-zinc-900 ">Customer action</th>
              <th className="text-left px-4 py-3 font-semibold text-zinc-900 ">Kora cost</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 ">
            {[
              ['Sign in and browse requests', 'Free'],
              ['Reply to your team, reopen, attach files', 'Free'],
              ['Raise a new request', '1 credit ($0.10), same as any ticket'],
              ['Download or delete their data', 'Free'],
              ['Extra seats for the portal', 'None: Kora has no seat fees'],
            ].map(([a, c]) => (
              <tr key={a}>
                <td className="px-4 py-3 font-medium text-zinc-900 ">{a}</td>
                <td className="px-4 py-3 text-zinc-600 ">{c}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mb-3">
        How to set it up
      </h2>
      <ol className="space-y-2 text-sm text-zinc-600 mb-6 list-decimal pl-5">
        <li>Sign in to Kora and open Settings, then switch the customer portal on.</li>
        <li>Choose open or invite-only sign-in and set your request caps.</li>
        <li>Share your portal link, usekora.app/p/portal/your-company, with customers.</li>
      </ol>
      <p className="text-sm text-zinc-600 leading-relaxed mb-6">
        There is no DNS change, approval step or sales call. Custom domains and SSO are on the{' '}
        <Link to="/roadmap" className="font-medium text-blue-600 hover:underline">roadmap</Link>, not available yet.
      </p>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mb-4">
        Frequently asked questions
      </h2>
      <div className="divide-y divide-zinc-100 border-y border-zinc-100 mb-8">
        {FAQS.map((faq) => (
          <details key={faq.q} className="group py-3">
            <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-medium text-zinc-900 ">
              {faq.q}
              <span className="text-zinc-400 group-open:rotate-45 transition-transform text-lg leading-none">+</span>
            </summary>
            <p className="mt-2 text-sm text-zinc-500 leading-relaxed">{faq.a}</p>
          </details>
        ))}
      </div>

      <div className="rounded-2xl border border-blue-100 bg-blue-50/50 p-5">
        <p className="text-sm text-zinc-700 leading-relaxed">
          Related: <Link to="/knowledge-base-software" className="font-medium text-blue-600 hover:underline">help center software</Link>,{' '}
          <Link to="/sla-tracking-software" className="font-medium text-blue-600 hover:underline">SLA tracking</Link>,{' '}
          <Link to="/pricing" className="font-medium text-blue-600 hover:underline">how Kora&apos;s credits work</Link>.
        </p>
      </div>
    </ContentPage>
  );
}
